import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, ExperimentalPendingTasks, inject, Injector, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { GenericDynamicComponent } from 'src/app/core/classes/DynamicComponent';
import { ContentfulPage } from 'src/app/core/interfaces/Page/page-contentful.interface';
import { AppComparisonSeriesComponent } from 'src/app/shared/components/builder/comparison-series/comparison-serie.component';
import { AppComparisonComponent } from 'src/app/shared/components/builder/comparison/comparison.component';
import { AppModalCarrouselComponent } from 'src/app/shared/components/builder/modal-carousel/modal-carrousel.compoment';
import { createInjector } from 'src/app/shared/helpers/page-component.helper';
import { isBuilderPage, isContentfulPage } from 'src/app/shared/page-provider';
import { AnalyticsStoreService } from 'src/app/shared/services';
import { PageLayoutComponentService } from 'src/app/shared/services/layout/page-layout-component.service';
import { PohService } from 'src/app/shared/services/poh.service';
import { BuilderPage } from '../../core/interfaces';
import { JebbitService } from './../../shared/services/jebbit.service';
import { MetaTagsService } from './../../shared/services/meta-tags.service';

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  standalone: true,
  imports: [NgComponentOutlet, AppComparisonComponent, AppComparisonSeriesComponent, AppModalCarrouselComponent],
})
export class AppPageComponent implements OnInit {
  public selectedTemplate?: GenericDynamicComponent;
  public templateInjector?: Injector;

  private changeDetector = inject(ChangeDetectorRef);
  private pendingTasks = inject(ExperimentalPendingTasks);
  #logger = inject(GenericLogger);
  #injector = inject(Injector);
  #router = inject(Router);
  #pageLayoutComponentService = inject(PageLayoutComponentService);

  /**
   * Currently (Angular 18) this component is constructed every time the route changes
   * Before it was constructed only once and we had to handle router changes with a router obeservable
   * TODO: Investigate: We don't really understand why this is happening, so it may change again or cause issues
   * FIXME: This component handles the untyped router resolved data and has page provider specific code
   * We should type the data and allow injecting specific page resolvers like contentful
   */
  constructor(
    private pohService: PohService,
    private jebbitService: JebbitService,
    private route: ActivatedRoute,
    private metaService: MetaTagsService,
    private analyticsService: AnalyticsStoreService,
  ) {
    // We need to use the snapshot value by default
    const routeSnapshotPageData = this.route.snapshot.data.page;

    if (!routeSnapshotPageData) {
      // loading 404 has to be done in component due to Angular limitations
      // https://github.com/angular/angular/issues/17004
      this.#router.navigateByUrl('/not-found', {
        onSameUrlNavigation: 'reload',
        skipLocationChange: true,
      });
      return;
    }
    // Check if the PageResolver has returned a category page:
    if (isBuilderPage(routeSnapshotPageData)) {
      this.#logger.debug('Page component: Builder category page detected');
      this.setBuilderPage(routeSnapshotPageData);
    } else if (isContentfulPage(routeSnapshotPageData)) {
      this.#logger.debug('Page component: Contentful page detected');
      this.setContentfulPage(routeSnapshotPageData);
    }
  }

  private setBuilderPage(data: BuilderPage) {
    this.loadTemplate({ type: data.type, data: data });
    this.analyticsService.pg_content('builder');

    if (data.extraData?.metadata) {
      this.metaService.setMetaTags({
        description: data.extraData?.metadata.description ?? '',
        title: data.extraData?.metadata.title ?? '',
        keywords: data.extraData?.metadata.keywords ?? '',
        robots: data.extraData?.metadata.robots ?? [],
      });
    } else {
      this.metaService.setMetaTags({
        title: data.name ?? '',
        description: '',
      });
    }
  }

  private setContentfulPage(data: ContentfulPage) {
    this.loadTemplate({ type: data.type, data: data });
  }

  private async loadTemplate(page: any) {
    if (page.type) {
      const templateTask = this.pendingTasks.add();
      this.#logger.debug(`Loading dynamic template ${page.type}`);
      this.selectedTemplate = await this.#pageLayoutComponentService.getComponent(page.type);
      if (!this.selectedTemplate) {
        this.#logger.error('Error loading dynamic template component', page);
      } else {
        this.#logger.debug(`Loaded dynamic template ${page.type}`);
      }

      this.templateInjector = createInjector(this.#injector, page.data);
      this.changeDetector.detectChanges();
      templateTask();
    }
  }

  ngOnInit(): void {
    this.pohService.checkCoupons();
    this.jebbitService.captureJebbitMessage();
  }
}
